import { api } from '../../app/api/baseApi';
import type {
    CreateServiceAccountPayload,
    CreateServiceAccountResponse,
    UpdateServiceAccountPayload,
    ServiceAccountListResponse,
    ServiceAccountListPayload,
    ServiceAccount,
    ServiceAccountList,
    RevokeServiceAccountResponse,
} from '../../types/serviceAccount';
import dayjs from 'dayjs';

export const serviceAccountApi = api.injectEndpoints({
    endpoints: (builder) => ({
        createServiceAccount: builder.mutation<{ data: CreateServiceAccountResponse }, CreateServiceAccountPayload>({
            query: (body) => ({
                url: 'iam/v1/serviceaccounts',
                method: 'POST',
                body,
            }),
        }),
        updateServiceAccount: builder.mutation<void, UpdateServiceAccountPayload>({
            query: ({ serviceAccountId, ...patch }) => ({
                url: `iam/v1/serviceaccounts/${encodeURIComponent(serviceAccountId)}`,
                method: 'PATCH',
                body: patch,
            }),
        }),
        deleteServiceAccount: builder.mutation<RevokeServiceAccountResponse, { serviceAccountIds: string[] }>({
            query: (body) => ({
                url: 'iam/v1/serviceaccounts/revoke',
                method: 'POST',
                body,
            }),
            invalidatesTags: (result, error) =>
                !error && result?.results?.some((account) => account.status === 'SUCCESS') ? ['ServiceAccounts'] : [],
        }),
        resumeServiceAccount: builder.mutation<void, string>({
            query: (serviceAccountId) => ({
                url: `iam/v1/serviceaccounts/${encodeURIComponent(serviceAccountId)}/resume`,
                method: 'PATCH',
            }),
            invalidatesTags: (_result, error) => (!error ? ['ServiceAccounts'] : []),
        }),
        suspendServiceAccount: builder.mutation<void, string>({
            query: (serviceAccountId) => ({
                url: `iam/v1/serviceaccounts/${encodeURIComponent(serviceAccountId)}/suspend`,
                method: 'PATCH',
            }),
            invalidatesTags: (_result, error) => (!error ? ['ServiceAccounts'] : []),
        }),
        getServiceAccounts: builder.query<
            ServiceAccountList,
            { payload: ServiceAccountListPayload; realmPrefix?: string; organizationId?: string }
        >({
            query: ({ payload, realmPrefix, organizationId }) => ({
                url: 'iam/v1/serviceaccounts/search',
                method: 'POST',
                body: payload,
                headers: {
                    ...(realmPrefix ? { 'x-realm': realmPrefix } : {}),
                    ...(organizationId ? { organizationId } : {}),
                },
            }),
            providesTags: ['ServiceAccounts'],
            transformResponse: (
                response: ServiceAccountListResponse | { data: ServiceAccountListResponse }
            ): ServiceAccountList => {
                const data = 'data' in response ? response.data : response;

                return {
                    records:
                        data.serviceAccounts?.map(
                            (serviceAccount): ServiceAccount => ({
                                ...serviceAccount,
                                name: serviceAccount.name,
                                clientId: serviceAccount.serviceAccountId,
                                expirationDate: serviceAccount.expiresAt
                                    ? dayjs(serviceAccount.expiresAt).format('MMM D, YYYY')
                                    : null,
                                description: serviceAccount?.description ?? '',
                                isActive: serviceAccount.status === 'ACTIVE',
                                type: serviceAccount.customerType === 'ORG' ? 'Organization' : 'Realm',
                                realm: serviceAccount.realmName,
                                organization: serviceAccount.orgName,
                            })
                        ) ?? [],
                    total: data.total,
                };
            },
        }),
    }),
});

export const {
    useCreateServiceAccountMutation,
    useUpdateServiceAccountMutation,
    useDeleteServiceAccountMutation,
    useResumeServiceAccountMutation,
    useSuspendServiceAccountMutation,
    useLazyGetServiceAccountsQuery,
} = serviceAccountApi;
